import { isMultiApiClientInitialized } from "@oneapp/data";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { Routes, Route } from "react-router-dom";
import { AinaDoubtChatPage } from "../pages/AinaDoubtChatPage";
import { DoubtsPage1 } from "../pages/DoubtsPage1";
import { DoubtsPage2 } from "../pages/DoubtsPage2";
import { DOUBTS_ROUTES } from "../routes";
import { DoubtProvider } from "../lib/DoubtContext";
import { mapModuleDataToDoubtSession } from "../lib/doubtSession";
import { initializeApi, type ModuleDataDto } from "../lib/setupApi";
import { ensureDoubtsWebAnalytics } from "../lib/ensureAnalytics";

ensureDoubtsWebAnalytics();

export type DoubtsAppProps = {
  env?: string;
  restoreHostApi?: () => void;
  dataDTO: ModuleDataDto;
  /** Host-provided logout handler used by dashboard header avatar dropdown. */
  handleLogOut?: () => void;
};

export const DoubtsApp = ({
  env = "production",
  restoreHostApi,
  dataDTO,
  handleLogOut,
}: DoubtsAppProps) => {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: 1, refetchOnWindowFocus: false },
        },
      }),
    [],
  );

  const doubtSession = useMemo(
    () => ({ ...mapModuleDataToDoubtSession(dataDTO), handleLogOut }),
    [dataDTO, handleLogOut],
  );

  useEffect(() => {
    const hostAlreadyInitialized = isMultiApiClientInitialized();
    if (!hostAlreadyInitialized) {
      initializeApi(env, dataDTO);
    }
    return () => {
      if (!hostAlreadyInitialized && restoreHostApi) {
        restoreHostApi();
      }
    };
  }, [env, restoreHostApi, dataDTO]);

  return (
    <QueryClientProvider client={queryClient}>
      <DoubtProvider session={doubtSession}>
        <Routes>
          <Route index element={<DoubtsPage1 />} />
          <Route
            path={DOUBTS_ROUTES.AINA_CHAT}
            element={<AinaDoubtChatPage />}
          />
          <Route path={DOUBTS_ROUTES.DETAIL} element={<DoubtsPage2 />} />
        </Routes>
      </DoubtProvider>
    </QueryClientProvider>
  );
};
